/**
 * Presentation helpers for window rooms ("vani finestra").
 *
 * The whole app has to describe a window room the same way: number, decoded
 * floor, room name, then the frame (model code + model description) and the
 * finish with its measures. Keeping the formatting here means the linking page,
 * the project dashboard and the history list cannot drift apart.
 */

/** Ordinal names used by the floor decoding, index 0 being the ground floor. */
const FLOOR_ORDINALS = [
  'terra',
  'primo',
  'secondo',
  'terzo',
  'quarto',
  'quinto',
  'sesto',
  'settimo',
  'ottavo',
  'nono',
  'decimo'
]

/**
 * Turns the raw floor stored on the position into its spoken form:
 * -1 -> "Piano interrato", 0 -> "Piano terra", 1 -> "Piano primo", ...
 * Non numeric values are returned untouched, so a free text floor still shows.
 */
export const decodeFloor = (floor?: string | number | null): string => {
  if (floor === null || floor === undefined || floor === '') return ''

  const value = Number(floor)
  if (!Number.isFinite(value)) return String(floor)

  if (value < 0) {
    return value === -1 ? 'Piano interrato' : `Piano interrato ${Math.abs(value)}`
  }

  const ordinal = FLOOR_ORDINALS[value]
  return ordinal ? `Piano ${ordinal}` : `Piano ${value}`
}

export interface WindowRoomDescriptor {
  positionNumber?: string | number | null
  floor?: string | number | null
  description?: string | null
}

/**
 * "Vano Finestra 3 (Piano terra - Cucina)".
 * The label is passed in so the caller keeps control of the translation.
 */
export const formatWindowRoomTitle = (
  label: string,
  { positionNumber, floor, description }: WindowRoomDescriptor
): string => {
  const head = positionNumber !== undefined && positionNumber !== null && positionNumber !== ''
    ? `${label} ${positionNumber}`
    : label

  const details = [decodeFloor(floor), description].filter(Boolean)
  return details.length > 0 ? `${head} (${details.join(' - ')})` : head
}

export interface FrameDescriptor {
  brand?: string | null
  model?: string | null
  modelDescription?: string | null
}

/** "Finestre & Finestre MD110 - Finestra PVC - Due ante". */
export const formatFrameModel = ({ brand, model, modelDescription }: FrameDescriptor): string =>
  [[brand, model].filter(Boolean).join(' '), modelDescription].filter(Boolean).join(' - ')

export interface FinishDescriptor {
  articleDescription?: string | null
  dimensions?: string | null
  width?: string | number | null
  height?: string | number | null
}

/** "L 900.00 x H 2100.00", built from either a ready made string or width/height. */
export const formatDimensions = ({ dimensions, width, height }: FinishDescriptor): string => {
  if (dimensions) return dimensions
  if (width === null || width === undefined || width === '') return ''
  if (height === null || height === undefined || height === '') return ''
  return `L ${width} x H ${height}`
}

/** "Porta Finestra bicolore - Interno Bianco - L 900.00 x H 2100.00". */
export const formatFrameFinish = (finish: FinishDescriptor): string =>
  [finish.articleDescription, formatDimensions(finish)].filter(Boolean).join(' - ')
